import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronLeft, ChevronRight, Quote, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import "./HomeOpinion.css";

function readingTime(html) {
  const text = (html || "").replace(/<[^>]+>/g, "").trim();
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.round(words / 200));
}

export default function HomeOpinion() {
  const [data, setData] = useState(null);
  const trackRef = useRef(null);

  useEffect(() => {
    api.get("/opinion/public", { params: { limit: 12 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null));
  }, []);

  const scrollBy = (dir) => {
    const el = trackRef.current;
    if (!el) return;
    const w = el.clientWidth;
    el.scrollBy({ left: dir * w * 0.85, behavior: "smooth" });
  };

  if (!data || !data.items || data.items.length === 0) return null;
  const items = data.items;
  const featured = items.find((x) => x.featured) || items[0];
  const rest = items.filter((x) => x.id !== featured.id);

  return (
    <section className="opinion-mod" data-testid="home-opinion">
      <header className="opinion-head">
        <div className="opinion-head-left">
          <Quote size={28} className="opinion-quote-icon" />
          <div>
            <h2 className="opinion-title">OPINIÓN</h2>
            <p className="opinion-sub">Las voces que marcan la conversación</p>
          </div>
        </div>
        <Link to="/opinion" className="opinion-see-all" data-testid="opinion-see-all">
          Ver todas <ArrowRight size={14} />
        </Link>
      </header>

      <div className="opinion-grid">
        {/* Featured large card */}
        <article className="opinion-feat" data-testid={`opinion-feat-${featured.slug}`}>
          {featured.image && (
            <div className="opinion-feat-img-wrap">
              <img src={featured.image} alt={featured.title} loading="lazy" />
            </div>
          )}
          <div className="opinion-feat-body">
            <span className="opinion-tag">DESTACADO</span>
            <Link to={`/opinion/${featured.slug}`} className="opinion-feat-title-link" data-testid={`opinion-feat-link-${featured.slug}`}>
              <h3 className="opinion-feat-title">{featured.title}</h3>
            </Link>
            <p className="opinion-feat-lead">{featured.lead}</p>
            <div className="opinion-feat-author">
              {featured.author?.photo_url && (
                <img className="opinion-feat-author-photo" src={featured.author.photo_url} alt={featured.author.name} />
              )}
              <div>
                <p className="opinion-author-name">{featured.author?.name}</p>
                {featured.author?.role && <p className="opinion-author-role">{featured.author.role}</p>}
              </div>
              <span className="opinion-feat-time">{readingTime(featured.body)} MIN</span>
            </div>
          </div>
        </article>

        {/* Carousel of remaining cards */}
        <div className="opinion-carousel-wrap">
          <div className="opinion-carousel" ref={trackRef} data-testid="opinion-carousel">
            {rest.map((art) => (
              <Link
                key={art.id}
                to={`/opinion/${art.slug}`}
                className="opinion-card"
                data-testid={`opinion-card-${art.slug}`}
              >
                <div className="opinion-card-photo-wrap">
                  <img
                    src={art.author?.photo_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(art.author?.name || "?")}&size=200&background=ec4899&color=fff`}
                    alt={art.author?.name}
                    className="opinion-card-photo"
                  />
                </div>
                <div className="opinion-card-body">
                  <p className="opinion-card-author">{art.author?.name}</p>
                  {art.author?.role && <p className="opinion-card-role">{art.author.role}</p>}
                  <h4 className="opinion-card-title">{art.title}</h4>
                  {art.lead && <p className="opinion-card-lead">{art.lead}</p>}
                  <span className="opinion-card-read">Leer columna →</span>
                </div>
              </Link>
            ))}
          </div>
          {rest.length > 2 && (
            <>
              <button className="opinion-nav opinion-nav-left" onClick={() => scrollBy(-1)} aria-label="Anterior" data-testid="opinion-prev">
                <ChevronLeft size={20} />
              </button>
              <button className="opinion-nav opinion-nav-right" onClick={() => scrollBy(1)} aria-label="Siguiente" data-testid="opinion-next">
                <ChevronRight size={20} />
              </button>
            </>
          )}
        </div>
      </div>

      <div className="opinion-disclaimer" data-testid="opinion-disclaimer">
        <p>
          Las opiniones y comentarios expresados en este sector son responsabilidad exclusiva
          de sus participantes y no representan necesariamente la posición de este medio.
        </p>
        <p>
          Este espacio promueve la libertad de expresión, el respeto y la participación plural,
          dentro del marco de la normativa vigente.
        </p>
      </div>
    </section>
  );
}
